import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Heart, Server, Code, Sparkles, Coffee, Instagram, ArrowRight } from "lucide-react";

export default function SupportPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center px-6 py-12">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-4 rounded-full">
              <Heart className="w-12 h-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-slate-800">
            Schön, dass Sie da sind!
          </CardTitle>
          <p className="text-slate-600 mt-2 text-lg">
            Kurze Bitte: Unterstützen Sie uns bei der Weiterentwicklung
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Main Support Section */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border-2 border-emerald-200">
            <h3 className="text-xl font-semibold text-slate-800 mb-3 flex items-center">
              <Coffee className="w-6 h-6 mr-2 text-emerald-600" />
              Unterstützen Sie die Entwicklung
            </h3>
            <p className="text-slate-700 mb-4">
              Diese App wurde mit viel Herzblut entwickelt und wird kontinuierlich verbessert. 
              Ihre Unterstützung hilft dabei, die laufenden Kosten zu decken:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="flex items-start space-x-3 bg-white p-3 rounded-lg">
                <Server className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800 text-sm">Serverkosten</p>
                  <p className="text-xs text-slate-600">Hosting & Datenbank</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 bg-white p-3 rounded-lg">
                <Code className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800 text-sm">Entwicklungskosten</p>
                  <p className="text-xs text-slate-600">Zeit & Ressourcen</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 bg-white p-3 rounded-lg">
                <Sparkles className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800 text-sm">Updates & Features</p>
                  <p className="text-xs text-slate-600">Neue Funktionen</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 bg-white p-3 rounded-lg">
                <Heart className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800 text-sm">Wartung & Support</p>
                  <p className="text-xs text-slate-600">Bugfixes & Hilfe</p>
                </div>
              </div>
            </div>

            <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-4 mb-5">
              <p className="text-emerald-800 text-center font-medium">
                💚 Bereits mit nur <span className="text-xl font-bold">10€</span> helfen Sie uns enorm!
              </p>
            </div>

            {/* PayPal Button */}
            <a 
              href="https://www.paypal.com/donate/?hosted_button_id=WPK5YE395ZHUJ"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <button 
                className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a7.7 7.7 0 0 1-.32 1.433c-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9L7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81.298.34.563.729.774 1.107z"/>
                </svg>
                <span className="text-sm sm:text-base">Jetzt via PayPal unterstützen</span>
              </button>
            </a>
          </div>

          {/* Alternative Support - Instagram */}
          <div className="border-t-2 border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-3 text-center">
              Keine finanzielle Unterstützung möglich?
            </h3>
            <p className="text-slate-600 text-center mb-4">
              Helfen Sie uns mit Reichweite! Folgen Sie uns auf Instagram und teilen Sie die App mit Ihren Teampartnern:
            </p>
            
            <a 
              href="https://www.instagram.com/feelgoodkompass?igsh=MTF4c2h6Zno5ZzB5ag=="
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <button 
                className="w-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white font-semibold py-4 px-6 rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center"
              >
                <Instagram className="w-6 h-6 mr-3 flex-shrink-0" />
                <span className="text-lg">@feelgoodkompass folgen</span>
              </button>
            </a>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 text-center">
              💡 <strong>Hinweis:</strong> Sie können uns jederzeit auch später unter <strong>Profil</strong> unterstützen.
            </p>
          </div>

          {/* Continue Button */}
          <Button 
            onClick={() => navigate('/main-hub')}
            className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800"
          >
            Weiter zur App
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
